import {
  ClusterSizesPerClusterCodeItem,
  OperatorPerPlatformItem,
} from 'types/capacityManagement.types';

// One row of GET /size/:cloudProviderID: the size name plus one cell per operator cluster code
// that offers it.
export type CapacityMatrixRecord = {
  size: string;
  [operatorClusterCode: string]:
    ClusterSizesPerClusterCodeItem | string | undefined;
};

export type SizeResources = {
  memoryRequest: number;
  memoryLimit: number;
  cpu: number;
};

export type OperatorCell = {
  operator: OperatorPerPlatformItem;
  cell?: ClusterSizesPerClusterCodeItem;
  differs: boolean;
  difference?: string;
};

export type SizeSummary = {
  size: string;
  displayName: string;
  isCustom: boolean;
  inPortal: boolean;
  record: CapacityMatrixRecord;
  baseline?: SizeResources;
  operators: OperatorCell[];
  offeredCount: number;
  differingCount: number;
};

export type SizeFilter = 'all' | 'differs' | 'partial' | 'standard' | 'custom';

// Main sizes offered to customers in Cloud Portal.
export const PORTAL_SIZES = [
  'xsmall',
  'small',
  'medium',
  'large',
  'xlarge',
  '2xlarge',
];

// A cell this far from its baseline (either way) is more likely a typo than a deliberate choice.
const OUTLIER_RATIO = 4;

export function resourcesOf(
  cell: Pick<
    ClusterSizesPerClusterCodeItem,
    'memoryRequest' | 'memoryLimit' | 'cpu'
  >,
): SizeResources {
  return {
    memoryRequest: Number(cell.memoryRequest),
    memoryLimit: Number(cell.memoryLimit),
    cpu: Number(cell.cpu),
  };
}

export function sameResources(a: SizeResources, b: SizeResources) {
  return (
    a.memoryRequest === b.memoryRequest &&
    a.memoryLimit === b.memoryLimit &&
    a.cpu === b.cpu
  );
}

export function formatMemory({ memoryRequest, memoryLimit }: SizeResources) {
  return `${memoryRequest} / ${memoryLimit} GB`;
}

// The value most operators share; on a tie, the one seen first in operator order.
export function findBaseline(
  cells: ClusterSizesPerClusterCodeItem[],
): SizeResources | undefined {
  const tally = new Map<string, { resources: SizeResources; count: number }>();
  cells.forEach(cell => {
    const resources = resourcesOf(cell);
    const key = `${resources.memoryRequest}/${resources.memoryLimit}/${resources.cpu}`;
    const entry = tally.get(key) ?? { resources, count: 0 };
    entry.count += 1;
    tally.set(key, entry);
  });
  let best: { resources: SizeResources; count: number } | undefined;
  tally.forEach(entry => {
    if (!best || entry.count > best.count) best = entry;
  });
  return best?.resources;
}

function signed(value: number) {
  return `${value > 0 ? '+' : '−'}${Math.abs(value)}`;
}

function percentChange(value: number, base: number) {
  return base ? Math.round(((value - base) / base) * 100) : 0;
}

// "−50% on all" when every resource moved by the same share, else the fields that differ.
export function describeDifference(
  value: SizeResources,
  baseline: SizeResources,
) {
  const request = percentChange(value.memoryRequest, baseline.memoryRequest);
  const limit = percentChange(value.memoryLimit, baseline.memoryLimit);
  const cpu = percentChange(value.cpu, baseline.cpu);
  if (
    request !== 0 &&
    Math.abs(request - limit) <= 3 &&
    Math.abs(request - cpu) <= 3
  ) {
    return `${request > 0 ? '+' : '−'}${Math.abs(request)}% on all`;
  }
  const parts: string[] = [];
  if (value.memoryRequest !== baseline.memoryRequest) {
    parts.push(
      `req ${signed(value.memoryRequest - baseline.memoryRequest)} GB`,
    );
  }
  if (value.memoryLimit !== baseline.memoryLimit) {
    parts.push(`limit ${signed(value.memoryLimit - baseline.memoryLimit)} GB`);
  }
  if (value.cpu !== baseline.cpu) {
    parts.push(`CPU ${signed(value.cpu - baseline.cpu)}`);
  }
  return parts.join(' · ');
}

export function buildSizeSummaries(
  records: CapacityMatrixRecord[],
  operators: OperatorPerPlatformItem[],
): SizeSummary[] {
  return records.map(record => {
    const cellOf = (operator: OperatorPerPlatformItem) =>
      record[operator.clusterCode] as
        ClusterSizesPerClusterCodeItem | undefined;
    const offered = operators
      .map(cellOf)
      .filter((cell): cell is ClusterSizesPerClusterCodeItem => !!cell);
    const baseline = findBaseline(offered);

    const operatorCells = operators.map((operator): OperatorCell => {
      const cell = cellOf(operator);
      const differs =
        !!cell && !!baseline && !sameResources(resourcesOf(cell), baseline);
      return {
        operator,
        cell,
        differs,
        difference:
          differs && cell && baseline
            ? describeDifference(resourcesOf(cell), baseline)
            : undefined,
      };
    });

    // Same rule the backend uses to refuse a delete: a size is a main (standard) size unless it
    // is both custom and deletable. Some main sizes are stored with isCustomSize set.
    const isCustom =
      offered.length > 0 &&
      offered.every(cell => cell.isCustomSize && cell.canBeDeleted);
    return {
      size: record.size,
      displayName: offered[0]?.displayName ?? '',
      isCustom,
      inPortal: !isCustom && PORTAL_SIZES.includes(record.size),
      record,
      baseline,
      operators: operatorCells,
      offeredCount: offered.length,
      differingCount: operatorCells.filter(item => item.differs).length,
    };
  });
}

export function matchesSizeFilter(summary: SizeSummary, filter: SizeFilter) {
  switch (filter) {
    case 'differs':
      return summary.differingCount > 0;
    case 'partial':
      return summary.offeredCount < summary.operators.length;
    case 'standard':
      return !summary.isCustom;
    case 'custom':
      return summary.isCustom;
    default:
      return true;
  }
}

export type CapacityOutlier = {
  size: string;
  operator: string;
  value: SizeResources;
  baseline: SizeResources;
};

// Cells whose memory limit is more than OUTLIER_RATIO times away from the size's baseline.
export function findOutliers(summaries: SizeSummary[]): CapacityOutlier[] {
  return summaries.flatMap(summary =>
    summary.operators.flatMap(({ operator, cell, differs }) => {
      if (!differs || !cell || !summary.baseline) return [];
      const ratio = Number(cell.memoryLimit) / summary.baseline.memoryLimit;
      return ratio > OUTLIER_RATIO || ratio < 1 / OUTLIER_RATIO
        ? [
            {
              size: summary.size,
              operator: operator.clusterCode,
              value: resourcesOf(cell),
              baseline: summary.baseline,
            },
          ]
        : [];
    }),
  );
}

export type DivergentOperator = {
  clusterCode: string;
  differing: number;
  offered: number;
};

// Operators that differ from the baseline on at least half of the sizes they offer (and at least
// three of them): usually a deliberately different node shape rather than drift.
export function findDivergentOperators(
  summaries: SizeSummary[],
): DivergentOperator[] {
  const byOperator = new Map<string, DivergentOperator>();
  summaries.forEach(summary => {
    // A size offered by one operator has nothing to differ from.
    if (summary.offeredCount < 2) return;
    summary.operators.forEach(({ operator, cell, differs }) => {
      if (!cell) return;
      const entry = byOperator.get(operator.clusterCode) ?? {
        clusterCode: operator.clusterCode,
        differing: 0,
        offered: 0,
      };
      entry.offered += 1;
      if (differs) entry.differing += 1;
      byOperator.set(operator.clusterCode, entry);
    });
  });
  return Array.from(byOperator.values()).filter(
    entry => entry.differing >= 3 && entry.differing / entry.offered >= 0.5,
  );
}
